"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import styles from "../../../styles/components/Navbar.module.scss";
import LocaleSwitcher from "./LocaleSwitcher";
import companyLogoHeaderDark from '../../assets/logo/putz-estrich-logo-aus-baumeisterhand.svg';

interface NavbarProps {
  showJobs?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ showJobs = false }) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const t = useTranslations("NavbarLinks");

  const toggleMenu = (menu: string) => {
    setOpenMenu((prevMenu) => (prevMenu === menu ? null : menu)); // Schließt andere Submenus automatisch
  };

  useEffect(() => {
    setOpenMenu(null);
    setIsMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!openMenu || isMobileOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInside =
        servicesRef.current?.contains(target) ||
        aboutRef.current?.contains(target);

      if (!clickedInside) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu, isMobileOpen]);

  const isDarkTheme = true;

  return (
    <nav className={`${styles.navbarBackground} ${isDarkTheme ? "bg-white" : "white"} shadow-lg w-full z-10 top-0 fixed`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="text-black font-bold text-lg">
              <Image priority src={companyLogoHeaderDark} alt="Logo" height={0} width={0} style={{ width: "150px", height: "auto" }} />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex space-x-6">
            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button onClick={() => toggleMenu("services")} className="px-3 py-2 uppercase text-sm font-bold text-bitben-violet hover:text-bitben-green">
                {t("link_services")}
              </button>
              {openMenu === "services" && (
                <div className="absolute left-0 mt-2 min-w-max bg-white shadow-lg rounded-lg py-2">
                  <Link href="/services/innenputze" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Innenputze</Link>
                  <Link href="/services/aussenputze" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Außenputze</Link>
                  <Link href="/services/estrich-bodentechnik" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Estrich & Bodentechnik</Link>
                  <Link href="/services/waermedaemmverbundsysteme" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Wärmedämmverbundsysteme</Link>
                  <Link href="/services/geruestbau" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Gerüstbau</Link>
                  <Link href="/services/fensterbaenke-mauerabdeckungen" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Fensterbänke & Mauerabdeckungen</Link>
              
                </div>
              )}
            </div>

            {/* About Dropdown */}
            <div className="relative" ref={aboutRef}>
              <button onClick={() => toggleMenu("about")} className="px-3 py-2  text-sm uppercase font-bold text-bitben-violet hover:text-bitben-green">
                {t("link_about")}
              </button>
              {openMenu === "about" && (
                <div className="absolute right-0 mt-2 min-w-max bg-white shadow-lg rounded-lg py-2">
                  <Link href="/about/company" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Über unser Unternehmen</Link>
                  <Link href="/about/team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Ihre Ansprechpartner</Link>
                  {showJobs && (
                    <Link href="/about/jobs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Karriere & Jobs</Link>
                  )}
                </div>
              )}
            </div>

            <Link href="/projects" className="px-3 py-2 uppercase text-sm font-bold text-bitben-violet hover:text-bitben-green">
              {t("link_projects")}
            </Link>
            <Link href="/contact" className="px-3 py-2 uppercase text-sm font-bold text-bitben-violet hover:text-bitben-green">
              {t("link_contact")}
            </Link>
              {/* <LocaleSwitcher /> */}
         
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              type="button"
              aria-label={isMobileOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={isMobileOpen}
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="p-2 rounded-md text-company-color-primary hover:text-white hover:bg-company-color-primary"
            >
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className="lg:hidden bg-white shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link href="/" className="block px-3 py-2 rounded-md font-medium text-base text-gray-900 hover:bg-gray-100 hover:text-bitben-green">
              {t("link_home")}
            </Link>

            {/* Services Mobile Dropdown */}
            <div>
              <button onClick={() => toggleMenu("services")} className="block w-full text-left px-3 py-2 rounded-md font-medium text-base text-gray-900 hover:bg-gray-100">
                {t("link_services")}
              </button>
              {openMenu === "services" && (
                <div className="pl-4">
                  <Link href="/services/innenputze" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Innenputze</Link>
                  <Link href="/services/aussenputze" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Außenputze</Link>
                  <Link href="/services/estrich-bodentechnik" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Estrich & Bodentechnik</Link>
                  <Link href="/services/waermedaemmverbundsysteme" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Wärmedämmverbundsysteme</Link>
                  <Link href="/services/geruestbau" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Gerüstbau</Link>
                  <Link href="/services/fensterbaenke-mauerabdeckungen" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Fensterbänke & Mauerabdeckungen</Link>
                </div>
              )}
            </div>
             {/* Projects */}
            <div>
            <Link href="/projects" className="block px-3 py-2 rounded-md font-medium text-base text-gray-900 hover:bg-gray-100 hover:text-bitben-green">
              {t("link_projects")}
            </Link>
            </div>
            {/* About Mobile Dropdown */}
            <div>
              <button onClick={() => toggleMenu("about")} className="block w-full text-left px-3 py-2 rounded-md font-medium text-base text-gray-900 hover:bg-gray-100">
                {t("link_about")}
              </button>
              {openMenu === "about" && (
                <div className="pl-4">
                  <Link href="/about/company" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Über unser Unternehmen</Link>
                  <Link href="/about/team" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Ihre Ansprechpartner</Link>
                  {showJobs && (
                    <Link href="/about/jobs" className="block px-3 py-2 text-gray-700 hover:bg-gray-100">Karriere & Jobs</Link>
                  )}
                </div>
              )}
            </div>
            <Link href="/contact" className="block px-3 py-2 rounded-md font-medium text-base text-gray-900 hover:bg-gray-100 hover:text-bitben-green">
              {t("link_contact")}
            </Link>
            {/* Locale Switcher */}
            {/* <div className="px-3 py-2">
              <LocaleSwitcher />
            </div> */}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
